import { estimationSelector } from "@/redux/selectors/estimation.selector";
import { useSelector } from "@/redux/store";
import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { TriangleAlert } from "lucide-react";
import type { LineItemIssue } from "./utils/line-item-issue";
import { errorTextFromTuple } from "./utils/line-item-issue";
import { LineSheetsHeaderSection } from "./details-section/LineSheets";

interface IssueProps {
  icon: ReactNode;
  label: string;
  description: string;
  descriptionClassName: string;
}

const Issue = ({ icon, label, description, descriptionClassName }: IssueProps) => {
  return (
    <div className="flex flex-row items-center space-x-1">
      <div className="flex flex-row items-center space-x-2 whitespace-nowrap">
        <div className="h-4 w-4">{icon}</div>
        <p className="text-sm">{label}</p>
      </div>
      <p className={`rounded-sm px-2 py-1 text-sm ${descriptionClassName}`}>{description}</p>
    </div>
  );
};

export const LineItemIssues = () => {
  const lineItemDetail = useSelector(estimationSelector.lineItemDetail);
  const rightSidebarCurrentTab = useSelector(estimationSelector.rightSidebarCurrentTab);
  const xactimateMapping = useSelector(estimationSelector.lineItemXactimateMapping);

  const issuesList = useMemo(() => {
    if (!rightSidebarCurrentTab?.lineItem || !lineItemDetail?.tuple) return [];

    return errorTextFromTuple(rightSidebarCurrentTab.lineItem, lineItemDetail.tuple.lowConfidenceArray ?? [], xactimateMapping ?? []);
  }, [rightSidebarCurrentTab?.lineItem, lineItemDetail?.tuple, xactimateMapping]);

  const getIssue = useCallback((issue: LineItemIssue, description: string) => {
    switch (issue) {
      case "name":
        return (
          <Issue
            icon={<img src="/line-name-issue.svg" />}
            label=""
            description={description}
            descriptionClassName="text-red-500 bg-red-50"
          />
        );
      case "quantity":
        return (
          <Issue
            icon={<img src="/quantity-issue.svg" />}
            label=""
            description={description}
            descriptionClassName="text-yellow-500 bg-yellow-50"
          />
        );
      case "confidence":
        return (
          <Issue
            icon={<img src="/confidence-issue.svg" />}
            label=""
            description={description}
            descriptionClassName="text-blue-500 bg-blue-50"
          />
        );
      case "unit":
        return (
          <Issue
            icon={<img src="/line-name-issue.svg" />}
            label=""
            description={description}
            descriptionClassName="text-red-500 bg-red-50"
          />
        );
      default:
        return <></>;
    }
  }, []);

  const issueExplanation = rightSidebarCurrentTab?.lineItem?.issueExplanation;

  if (!issuesList.length && !issueExplanation) return <></>;

  return (
    <div className="flex flex-col space-y-4">
      <LineSheetsHeaderSection title="Issues">
        <TriangleAlert className="h-5 w-5 text-yellow-500" />
      </LineSheetsHeaderSection>
      <div className="flex flex-col space-y-4 px-4">
        {issueExplanation && (
          <div className="rounded-lg border border-yellow-400 bg-yellow-50 p-3">
            <p className="text-sm font-medium text-yellow-800">Issue Explanation</p>
            <p className="mt-1 text-sm text-yellow-700">{issueExplanation}</p>
          </div>
        )}
        {issuesList.map((current, index) => {
          return <div key={index}>{getIssue(current.issue, current.description)}</div>;
        })}
      </div>
    </div>
  );
};
